import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemIcon,
  Menu,
  MenuItem,
  ListItemText,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Alert,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { lighten, darken, alpha } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import LessonCard from "./LessonCard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";

import { handlePrintQuiz, handlePrint1X2Quiz } from "./printFunctions";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SchoolIcon from "@mui/icons-material/School";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUp";
import { collection, query, where, getDocs } from "firebase/firestore";
import { SubjectsList, GradeLevels } from "./BankFilters";
import { db } from "./firebase";

import BallotIcon from "@mui/icons-material/Ballot";
import ShortTextIcon from "@mui/icons-material/ShortText";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import Grid3x3Icon from "@mui/icons-material/Grid3x3";
// Import TeacherQuizDisplay
import TeacherQuizDisplay from "./TeacherQuizDisplay";
import getListStyles from "./listStyles";

// Subject Icons
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CalculateIcon from "@mui/icons-material/Calculate";
import TranslateIcon from "@mui/icons-material/Translate";
import PublicIcon from "@mui/icons-material/Public";

import PsychologyIcon from "@mui/icons-material/Psychology";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import BrushIcon from "@mui/icons-material/Brush";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import BusinessIcon from "@mui/icons-material/Business";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import ChurchIcon from "@mui/icons-material/Church";

import ScienceIcon from "@mui/icons-material/Science"; // For Chemistry
import WaveIcon from "@mui/icons-material/Waves"; // For Physics
import BiotechIcon from "@mui/icons-material/Biotech"; // For Biology
import ForestIcon from "@mui/icons-material/Forest"; // For Natural Science
import Logger from "./Logger";

const QuizBank = ({ open, onClose, user, styles, theme }) => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showTeacherQuizDisplay, setShowTeacherQuizDisplay] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuQuiz, setMenuQuiz] = useState(null);

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [menuPosition, setMenuPosition] = useState(null);

  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedQuizTypes, setSelectedQuizTypes] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const { listItemStyles, listItemIconStyles, listItemTextStyles } =
    getListStyles(theme, theme.palette.mode === "dark");

  const adminIds = [
    "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
    "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
    "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
  ];

  const handleQuizCardClick = (event, quiz) => {
    setMenuPosition({
      top: event.clientY + 2, // Add 2px offset
      left: event.clientX + 2, // Add 2px offset
    });
    setMenuQuiz(quiz);
  };

  // Add this close handler
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuQuiz(null);
  };

  useEffect(() => {
    const fetchQuizzes = async () => {
      setIsLoading(true);
      // Converting January 1st 2025 to milliseconds since epoch
      const startDate = new Date("2025-01-01").getTime();

      const quizQuery = query(
        collection(db, "quiz"),
        where("userId", "in", adminIds),
        where("createdAt", ">=", startDate),
        where("tags", "!=", null),
      );

      try {
        const querySnapshot = await getDocs(quizQuery);
        const fetchedQuizzes = querySnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((quiz) => quiz.tags?.length > 0);
        Logger.log("Start date timestamp:", startDate); // Add this to verify
        Logger.log("Fetched quizzes:", fetchedQuizzes); // Add this to verify
        setQuizzes(fetchedQuizzes);
      } catch (error) {
        console.error("Error fetching quizzes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchQuizzes();
    }
  }, [open]);

  const handleQuizUpdate = (updatedQuizData) => {
    // Update the selected quiz in state
    setSelectedQuiz((prevQuiz) => ({
      ...prevQuiz,
      results: updatedQuizData,
    }));
  };

  const filteredQuizzes = quizzes
    .filter((quiz) => {
      let matchesSubject = true;
      let matchesGrade = true;
      let matchesQuizType = true;

      if (selectedSubjects.length > 0) {
        matchesSubject = selectedSubjects.some((subject) =>
          quiz.tags?.includes(subject),
        );
      }
      if (selectedGrades.length > 0) {
        matchesGrade = selectedGrades.some((grade) =>
          quiz.tags?.includes(grade),
        );
      }
      if (selectedQuizTypes.length > 0) {
        matchesQuizType = selectedQuizTypes.includes(quiz.questionType);
      }
      return matchesSubject && matchesGrade && matchesQuizType;
    })
    .sort((a, b) =>
      selectedSubject ? 0 : (b.upvotes || 0) - (a.upvotes || 0),
    );

  const handleQuizClick = (quiz, viewType) => {
    if (viewType === "interactive") {
      const formattedQuizName = quiz.quizName
        .toLowerCase()
        .replace(/\s+/g, "-");
      navigate(`/app/quiz/${quiz.id}/${formattedQuizName}`);
      onClose();
    } else {
      setSelectedQuiz(quiz);
      setShowTeacherQuizDisplay(true);
    }
  };

  const handleTeacherQuizDisplayClose = () => {
    setShowTeacherQuizDisplay(false);
    setSelectedQuiz(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxWidth: "1350px",
            position: "relative",
            borderRadius: "25px",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#192734" : "#fff",
          },
        }}
      >
        <Alert
          severity="error"
          sx={{
            width: { xs: "100%", md: "200px" },
            margin: "0 auto",
            //  background: "#a4c4a42e",
            border: "1px solid #d7454426",
            //background: "none",
            padding: "4px",
            borderRadius: 4,
            position: "absolute",
            bottom: 20,
            left: 20,

            // left: "50%",
            // transform: "translateX(-50%)",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              fontWeight: "600",
              color: "#d84746",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              marginRight: 1,
              marginTop: 0,
              marginLeft: 1,
              fontSize: "1.1rem",
            },
          }}
        >
          <span>Fler quiz är på väg!</span>
        </Alert>
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "700", fontSize: "1.5rem" }}
            >
              Quiz-banken
            </Typography>
          </Box>
          {selectedGrades.includes("Nationella provet") && (
            <Chip
              label="Nationella provet"
              sx={{
                position: "absolute",
                top: "42px",
                left: "190px",
                transform: "rotate(-12deg)",
                backgroundColor: "#ffd700",
                color: "#000",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#ffc700",
                },
              }}
            />
          )}{" "}
          {selectedGrades.includes("Påsk") && (
            <img
              style={{
                position: "absolute",
                transform: "rotate(12deg)",
                top: "12px",
                left: selectedGrades.includes("Nationella provet")
                  ? "352px"
                  : "250px",
                width: "60px",
              }}
              src="/img/rabbit.png"
              alt="Påsk"
            />
          )}{" "}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Left Sidebar - Subject Filter */}
            <Grid item xs={12} md={3}>
              <SubjectsList
                selectedSubjects={selectedSubjects} // Changed prop
                setSelectedSubjects={setSelectedSubjects} // Changed prop
                setIsLoading={setIsLoading}
                theme={theme}
              />
            </Grid>

            {/* Right Content - Quizzes */}
            <Grid item xs={12} md={9}>
              {/* Grade Level Filters */}

              {/* Grade Level Filters */}
              <GradeLevels
                selectedGrades={selectedGrades} // Changed prop
                setSelectedGrades={setSelectedGrades} // Changed prop
                setIsLoading={setIsLoading}
                styles={styles}
                theme={theme}
                selectedSubject={selectedSubject}
                selectedSubjects={selectedSubjects}
                filteredItems={filteredQuizzes} // or filteredFlashcards
                bankType="quiz" // or "flashcards"
                quizTypeFilter={true} // ADD THIS
                selectedQuizTypes={selectedQuizTypes} // ADD THIS
                setSelectedQuizTypes={setSelectedQuizTypes} // ADD THIS
              />
              {/* Quiz Grid */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {isLoading ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <CircularProgress size={50} />
                    <Typography sx={{ mt: 2, color: "text.secondary" }}>
                      Laddar quiz...
                    </Typography>
                  </Box>
                ) : filteredQuizzes.length === 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "400px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <img
                      src="/img/empty.png"
                      alt="Empty"
                      style={{ width: "200px", marginBottom: "24px" }}
                    />
                    <Typography
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        fontWeight: "500",
                        fontSize: "1.3rem",
                        width: { xs: "100%", md: "70%" },
                      }}
                    >
                      Här var det tomt! Vi jobbar på att fylla på Quiz-banken.
                      Titta in snart igen! :)
                    </Typography>
                    <Button
                      variant="print"
                      onClick={() => {
                        setSelectedSubjects([]);
                        setSelectedGrades([]);
                      }}
                      sx={{ mt: 4 }}
                    >
                      Rensa filter
                    </Button>
                  </Box>
                ) : (
                  filteredQuizzes.map((quiz) => (
                    <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                      {/* Most of the code remains the same, just updating the Box components */}
                      {/* Main container */}
                      <Box
                        onClick={(e) => handleQuizCardClick(e, quiz)}
                        sx={{
                          ...styles.freshBox,
                          borderRadius: "18px",
                          padding: "0px",
                          height: "230px",

                          display: "flex",
                          flexDirection: "column",
                          cursor: "pointer",
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          border:
                            theme.palette.mode === "dark"
                              ? undefined
                              : "1px solid #f3f0f0",
                          "&:hover": {
                            border:
                              theme.palette.mode === "dark"
                                ? undefined
                                : `1px solid ${theme.palette.primary.main}`,
                            transform: "translateY(-4px)",
                            "& .button-container": {
                              opacity: 1,
                            },
                            // "& > div:nth-child(3)": {
                            //   "& > div:first-child": {
                            //     filter: "blur(3px)",
                            //   },
                            // },
                            // "& > div:nth-child(3) > div:last-child": {
                            //   // This targets just the bottom container
                            //   filter: "blur(3px)",
                            // },
                          },
                        }}
                      >
                        <IconButton
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            zIndex: 9999999,
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        {/* Background Image */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            //backgroundImage:
                            //  "url(https://sportkuriren.se/wp-content/uploads/np.jpg)",
                            background:
                              theme.palette.mode === "dark"
                                ? "#384b63"
                                : "rgb(250 250 250)",
                            boxShadow:
                              "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />

                        {/* Dark Overlay */}
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            //         bottom: 0,
                            //         background: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)),
                            // linear-gradient(to top, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 100%)`,
                            background: "#7b8ba0",
                            zIndex: 1,
                          }}
                        />

                        {/* Content Container */}
                        <Box
                          sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                          }}
                        >
                          {/* Quiz Name */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: "16px", // Adjust this value based on your bottom container height
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              zIndex: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{
                                fontSize: "1.2rem",
                                fontWeight: "600",
                                textAlign: "center",
                                p: 2,
                                position: "relative",
                              }}
                            >
                              {quiz.quizName}
                            </Typography>
                          </Box>

                          {/* Buttons container */}
                          {/* Buttons container */}

                          {/* Bottom container */}
                          <Box
                            sx={{
                              mt: "auto",
                              p: 1.5,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            {/* Likes section */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <ThumbUpOffAltIcon
                                sx={{
                                  fontSize: "17px",
                                  color: "text.secondary",
                                }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  lineHeight: 1,
                                  fontWeight: "600",
                                  color: "text.secondary",
                                }}
                              >
                                {quiz.upvotes || 0}
                              </Typography>
                            </Box>

                            {/* Question type section */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {quiz.questionType === "multipleChoice" ? (
                                <BallotIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                              ) : quiz.questionType === "fill-in-the-blank" ? (
                                <ShortTextIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                              ) : quiz.questionType === "exam" ? (
                                <SchoolIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                              ) : quiz.questionType === "current-events" ? (
                                <Grid3x3Icon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                              ) : quiz.questionType === "true-or-false" ? (
                                <FactCheckIcon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                />
                              ) : null}
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                              >
                                {quiz.questionType === "multipleChoice"
                                  ? "Flervalsfrågor"
                                  : quiz.questionType === "fill-in-the-blank"
                                    ? "Lucktext"
                                    : quiz.questionType === "true-or-false"
                                      ? "Sant/Falskt"
                                      : quiz.questionType === "exam"
                                        ? "Prov"
                                        : quiz.questionType === "current-events"
                                          ? "1X2"
                                          : ""}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))
                )}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      <Menu
        open={Boolean(menuPosition)}
        onClose={() => setMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
          },
        }}
      >
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            handleQuizClick(menuQuiz, "view");
            handleMenuClose();
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <ListAltIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Redigera och skriv ut"
            secondary="Eller spara i ditt arkiv"
          />
        </MenuItem>
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            menuQuiz?.questionType === "current-events"
              ? handlePrint1X2Quiz(
                  menuQuiz.results,
                  menuQuiz.quizName,
                  menuQuiz.questionType,
                  theme.palette.primary.main,
                )
              : handlePrintQuiz(
                  menuQuiz.results,
                  menuQuiz.quizName,
                  menuQuiz.questionType,
                  theme.palette.primary.main,
                );
            handleMenuClose();
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <PrintIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText primary="Skriv ut" secondary="Skapa utskriftsversion" />
        </MenuItem>
        {menuQuiz?.questionType !== "current-events" && (
          <MenuItem
            sx={{
              ...styles.menuItemStyle,
              ...styles.menuItemStyleLarger,
            }}
            onClick={() => {
              handleQuizClick(menuQuiz, "interactive");
              handleMenuClose();
              setMenuPosition(null);
            }}
          >
            <ListItemIcon sx={{ mr: 1 }}>
              <PlayArrowIcon style={{ fontSize: "1.5rem" }} />
            </ListItemIcon>
            <ListItemText
              primary="Starta interaktivt quiz"
              secondary="Spela med poängräkning"
            />
          </MenuItem>
        )}
      </Menu>
      {/* TeacherQuizDisplay Dialog */}
      {showTeacherQuizDisplay && selectedQuiz && (
        <TeacherQuizDisplay
          quiz={selectedQuiz.results}
          quizId={selectedQuiz.id}
          quizName={selectedQuiz.quizName}
          user={user}
          styles={styles}
          theme={theme}
          questionType={selectedQuiz.questionType}
          onQuizUpdate={handleQuizUpdate}
          category={selectedQuiz.category}
          difficulty={selectedQuiz.difficulty}
          aiInstructions={selectedQuiz.aiInstructions}
          selectedLanguage={selectedQuiz.language}
          selectedModel={selectedQuiz.aiModel}
          includeTimer={selectedQuiz.includeTimer}
          duration={selectedQuiz.duration}
          onClose={handleTeacherQuizDisplayClose}
          selectedQuiz={selectedQuiz}
          onPrint={() =>
            selectedQuiz.questionType === "current-events"
              ? handlePrint1X2Quiz(
                  selectedQuiz.results,
                  selectedQuiz.quizName,
                  selectedQuiz.questionType,
                  theme.palette.primary.main,
                )
              : handlePrintQuiz(
                  selectedQuiz.results,
                  selectedQuiz.quizName,
                  selectedQuiz.questionType,
                  theme.palette.primary.main,
                )
          }
        />
      )}
    </>
  );
};

export default QuizBank;
